import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Field, Header, Input, Message, Modal, Toast, Icon, Items } from 'react-frontier';
import { Dropdown } from 'semantic-ui-react';
import { useUser } from '../hooks';
import { bindFormChange, bindClick, getAssetTypeIcon } from '../SiteUtil';
import { Expense, ExpenseType, ExpenseForm, EventDate, EventDateList, SetLoading, UserBankAccount, AssetType, Department, PaymentMethod } from '../SiteClasses';
import UploadBox, { UploadBoxRef } from './UploadBox';
import { Link } from 'react-router-dom';
import SearchEventModal from './SearchEventModal';
import SearchAccountModal from './SearchAccountModal';
import moment from 'moment';
import Validator from '../Validator';
import API from '../API';

enum Modals{
	EVENTS = 1,
	ACCOUNTS = 2,
	SEARCH_WORKER = 3,
	WORKER_ACCOUNTS = 4,
}

interface ModalProps {
	open: boolean,
	onClose: ()=>void,
	onCreate?: (expense: Expense)=>void,
	onUpdate?: (expense: Expense)=>void,
	expenseToEdit?: Expense,
	date_id?: number,
	event_name?: string,
}

var ExpenseModal = (props: ModalProps)=>{
	var { user } = useUser();
	var [loadError, setLoadError] = useState<string>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [form, setForm] = useState<ExpenseForm>(null);
	var [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>(null);
	var [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>(null);
	var [shownModal, setShownModal] = useState<number>(null);
	var [isEditing, setIsEditing] = useState<boolean>(false);
	var [accounts, setAccounts] = useState<UserBankAccount[]>(null);
	var [workerAccounts, setWorkerAccounts] = useState<UserBankAccount[]>(null);
	var [selectedFile, setSelectedFile] = useState<File>(null);
	var [fileTypeId, setFileTypeId] = useState<number>(null);
	var uploadBoxRef = useRef<UploadBoxRef>(null);
	
	var hasFixedEvent = props.date_id && props.event_name;

	useEffect(()=>{
		if(props.open){
			var editingMode = Boolean(props.expenseToEdit);
			setIsEditing(editingMode);
			
			if(editingMode){
				setForm({
					date_id: props.expenseToEdit.date_id || null,
					type_id: props.expenseToEdit.type_id,
					department_id: props.expenseToEdit.department_id,
					method_id: props.expenseToEdit.method_id,
					amount: props.expenseToEdit.amount,
					description: props.expenseToEdit.description,
					expense_title: props.expenseToEdit.expense_title,
					use_date_id: Boolean(props.expenseToEdit.date_id),
					event_name: props.expenseToEdit.event_name,
					account_id: props.expenseToEdit.account_id,
					account: props.expenseToEdit.account || null,
					is_reposition: props.expenseToEdit.account ? true : false,
				});
			}else{
				setForm({
					date_id: props.date_id || null,
					type_id: null,
					department_id: Department.OPERACIONES,
					method_id: null,
					amount: null,
					description: null,
					expense_title: null,
					use_date_id: hasFixedEvent ? true : false,
					event_name: props.event_name || null,
					account_id: null,
					is_reposition: false,
				});
			}
			setSelectedFile(null);
			setFileTypeId(null);
			if(uploadBoxRef.current){
				uploadBoxRef.current.clear();
			}
			getTypes();
		}
	}, [props.open, props.expenseToEdit]);

	var getTypes = async()=>{        
		API.getExpenseTypes().then(res=>{
			if(res.error) return setLoadError(res.message);
			return setExpenseTypes(res.data);
		}).catch(err=>{
			return setLoadError('Hubo un error al obtener los tipos. (GET-1)');
		})
		API.getPaymentMethods().then(res=>{
			if(res.error) return setLoadError(res.message);
			return setPaymentMethods(res.data);
		}).catch(err=>{
			return setLoadError('Hubo un error al obtener los métodos de pago. (GET-2)');
		})
	};

	var selectEvent = (event: EventDateList)=>{
		setForm(prev=>({
			...prev,
			date_id: event.date_id,
			event_name: event.event_name,
			use_date_id: true
		}));
		setShownModal(null);
	}

	var submit = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(form, {
			expense_title: [{ 
				rule: 'length', params: [3, 32], label: 'Título de gasto' 
			}],
			type_id: [{
				rule: 'empty', label: 'Tipo de gasto'
			}],
			method_id: [{
				rule: 'empty', label: 'Método de pago'
			}],
			department_id: [{
				rule: 'empty', label: 'Departamento'
			}],
			amount: [{
				rule: 'min', params: [1], label: 'Monto'
			}],
			...(form.is_reposition ? {
				account_id: [{
				rule: 'empty', label: 'Cuenta bancaria'
				}],
			} : {}),
			...(form.use_date_id ? {
				date_id: [{
					rule: 'empty', label: 'Evento'
				}],
			} : {}),
		});

		setModalPrompts(prompts);
		if(!valid) return;
		
		setLoading(true);
		
		var dateIdToSend = form.use_date_id ? form.date_id : null;
		var accountIdToSend = form.is_reposition ? form.account_id : null;
		
		if(isEditing && props.expenseToEdit){
			API.editExpense(props.expenseToEdit.expense_id, form.expense_title, dateIdToSend, form.type_id, form.method_id, form.department_id, form.amount, form.description, accountIdToSend).then(res=>{
				if(res.error) return setModalPrompts([res.message]);
				Toast.success('Gasto actualizado!');
				var updatedExpense: Expense = {
					...props.expenseToEdit,
					expense_title: form.expense_title,
					date_id: dateIdToSend,
					event_name: form.use_date_id ? form.event_name : null,
					type_id: form.type_id,
					type_name: expenseTypes.find(t => t.type_id === form.type_id)?.type_name,
					method_id: form.method_id,
					method_name: paymentMethods.find(m => m.method_id === form.method_id)?.method_name,
					amount: form.amount,
					description: form.description,
					account: form.is_reposition ? form.account :  null
				};
				props.onUpdate?.(updatedExpense);
				props.onClose();
			}).catch(err=>{
				setModalPrompts(['Hubo un error inesperado actualizando el gasto (LCL-ECM-2)']);
			}).finally(()=>{
				setLoading(false);
			});
		}else{
			API.createExpense(form.expense_title, dateIdToSend, form.type_id, form.method_id, form.department_id, form.amount, form.description, accountIdToSend, selectedFile, fileTypeId).then(async res=>{
				if(res.error) return setModalPrompts([res.message]);
				Toast.success('Gasto creado!');

				var newExpense: Expense = {
					expense_id: res.data.expense_id,
					expense_title: form.expense_title,
					date_id: dateIdToSend,
					event_name: form.use_date_id ? form.event_name : null,
					type_id: form.type_id,
					type_name: expenseTypes.find(t => t.type_id === form.type_id)?.type_name,
					method_id: form.method_id,
					method_name: paymentMethods.find(m => m.method_id === form.method_id)?.method_name,
					amount: form.amount,
					description: form.description,
					date_created: moment().unix(),
					paid: false,
					deleted: false,
					account: form.account,
					approved: res.data.approved,
					user_creator: user.user_id,
					user_creator_name: (user.first_name + ' ' +user.last_name),
					user_approved: res.data.approved ? user.user_id : null,
					user_approver_name: res.data.approved ? (user.first_name + ' ' +user.last_name) : null,
					rejected: false,
					reject_reason: null,
					reject_reason_name: null,
	
				};
				props.onCreate(newExpense);
			}).catch(err=>{
				setModalPrompts(['Hubo un error inesperado creando el gasto (LCL-ECM-1)']);
			}).finally(()=>{
				setLoading(false);
			});
		}
	}

	var handleToggle = (val: boolean)=>{
		setForm(prev=>({
			...prev,
			use_date_id: val,
			...(val ? {} : { date_id: null, event_name: null })
		}));
	}

	var handleRepositionToggle = (val: boolean)=>{
		setForm(prev=>({
			...prev,
			is_reposition: val,
			...(val ? { account_id: null, account: null } : {})
		}));
	};

	var onShowAccountsModal = async()=>{
		API.getUserBankAccounts().then(res=>{
			if(res.error) return Toast.error(res.message);
			setAccounts(res.data);
			setShownModal(Modals.ACCOUNTS);
		}).catch(err=>{
			return Toast.error('Hubo un error al obtener las cuentas. (GET-1)');
		});
	}

	var onAccountSelected = (a: UserBankAccount)=>{
		var f = {...form};
		f.account = a;
		f.account_id = a.account_id;
		setForm(f);
		setShownModal(null);
	}

var onSearchAccountSelected = (account: any)=>{
		var userAccount: UserBankAccount = {
			account_id: account.account_id,
			name_account: account.name_account,
			bank: account.bank,
			card_number: account.card_number,
			clabe: account.clabe,
			rfc: account.rfc,
			curp: account.curp,
			postal_code: account.postal_code,
		};
		onAccountSelected(userAccount);
	}

	var onFileSelected = (file: File)=>{
		var type : AssetType = null;
		if(!file){
			setSelectedFile(null);
			setFileTypeId(null);
			return;
		}
		if(file.type=='application/pdf'){
			type = AssetType.PDF;
		}else if(file.type.startsWith('image/')){
			type = AssetType.IMAGE;
		}
		if(!type) return Toast.error('El archivo no es un formato válido');
		setSelectedFile(file);
		setFileTypeId(type);
	}

	var onFormChange = bindFormChange(form, setForm);

	return <>
		<Modal open={props.open} onClose={props.onClose} size='tiny'>
			<Modal.Header>{isEditing ? 'Editar gasto' : 'Agregar gasto'}</Modal.Header>
			<Modal.Content>
				{!form || !expenseTypes || !paymentMethods ? (
					loadError ? (
						<Header text='Error' subtext={loadError} />
					) : (
						<Header loading text='Cargando' />
					)
				) : (
					<>
						<Input 
							label='Título' 
							placeholder='Título' 
							value={form.expense_title} 
							onChange={onFormChange('expense_title')}
						/>
						<>
							{!props.date_id ? <Checkbox 
								label='Vincular a evento'
								checked={form.use_date_id} 
								onChange={handleToggle} 
								style={{ marginBottom: 10 }}
							/>	: null}					
							{form.use_date_id && (
								<Field label='Evento' style={{ marginTop: 10 }}>
									<Input 
										value={form.event_name}
										placeholder='Selecciona un evento...'
										readonly
										button={
										<Button
											text='Seleccionar'
											onClick={bindClick(setShownModal, Modals.EVENTS)}
											disabled={hasFixedEvent ? true : false}
										/>
									}
									/>
								</Field>
							)}
						</>
						<Input 
							label='Monto' 
							placeholder='Monto de gasto' 
							value={form.amount}
							onChange={onFormChange('amount')}
						/>
						<Field label='Tipo'>
							<Dropdown 
								selection 
								value={form.type_id} 
								onChange={onFormChange('type_id', true)} 
								placeholder='Tipo de gasto' 
								options={expenseTypes.map(t=>({
									text: t.type_name,
									value: t.type_id,
									key: t.type_id
								}))} 
							/>
						</Field>
						<Field label='Método de pago'>
							<Dropdown 
								selection 
								value={form.method_id} 
								onChange={onFormChange('method_id', true)} 
								placeholder='Método de pago' 
								options={paymentMethods.map(m=>({
									text: m.method_name,
									value: m.method_id,
									key: m.method_id
								}))} 
							/>
						</Field>
						<Field label='Departamento'>
							<Dropdown
								selection
								value={form.department_id}
								onChange={onFormChange('department_id', true)}
								placeholder='Selecciona departamento'
								options={[
									{ key: Department.OPERACIONES, value: Department.OPERACIONES, text: 'Operaciones' },
									{ key: Department.COMERCIAL, value: Department.COMERCIAL, text: 'Comercial' },
									{ key: Department.DESARROLLO, value: Department.DESARROLLO, text: 'Desarrollo' },
									{ key: Department.RECURSOS_HUMANOS, value: Department.RECURSOS_HUMANOS, text: 'Recursos Humanos' },
									{ key: Department.MARKETING, value: Department.MARKETING, text: 'Marketing' },
									{ key: Department.CONTABILIDAD, value: Department.CONTABILIDAD, text: 'Contabilidad' },
								]}
							/>
						</Field>
						<Checkbox 
							label='Es una reposición'
							checked={form.is_reposition} 
							onChange={handleRepositionToggle} 
							style={{ marginBottom: 10 }}
						/>
						{!!form.is_reposition && (
							<Field label='Cuenta bancaria'>
								<Input
									value={
									[form.account?.name_account, form.account?.bank]
										.filter(Boolean)
										.join(' - ')
									}
									placeholder='Selecciona una cuenta...'
									readonly
									button={
										<Button
											text='Seleccionar'
											onClick={onShowAccountsModal}
										/>
									}
								/>
							</Field>
						)}
						<Input 
							label='Descripción' 
							placeholder='Descripción de gasto' 
							textarea 
							value={form.description} 
							onChange={onFormChange('description')}
						/>
						<Field label='Evidencia (Opcional)'>
							<UploadBox
								ref={uploadBoxRef}
								icon={getAssetTypeIcon(fileTypeId)}
								type={'normal'}
								text={selectedFile ? selectedFile.name : null}
								maxFileSize={2.5*1024*1024}
								onFile={onFileSelected}
							/>
						</Field>
						<Message style={{ marginTop: 15 }} type='error' list={modalPrompts} />
					</>
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={props.onClose} />
				<Button color="black" text={isEditing ? 'Guardar' : 'Crear'} onClick={submit} />
			</Modal.Actions>
		</Modal>
		<SearchEventModal
			open={shownModal===Modals.EVENTS}
			onClose={bindClick(setShownModal, null)}
			onSelected={selectEvent}
		/>
		<SearchAccountModal
			open={shownModal===Modals.SEARCH_WORKER}
			onClose={bindClick(setShownModal, null)}
			onSelected={onSearchAccountSelected}
		/>
		<Modal open={shownModal===Modals.ACCOUNTS || shownModal===Modals.WORKER_ACCOUNTS} onClose={bindClick(setShownModal, null)} size='mini'>
			<Modal.Header>Selecciona la cuenta</Modal.Header>
			<Modal.Content>
				<Items
					selectable
					data={shownModal===Modals.WORKER_ACCOUNTS ? workerAccounts : accounts}
					onSelected={(a)=>onAccountSelected(a)}
					render={a=>{
						return <div className="ar card placeholder account">
							<Icon name={a.clabe ? 'landmark' : 'credit-card'} />
							<div className="left">
								<div className="title">
									{a.clabe ? <>
										<b>SPEI</b> **{a.clabe.slice(-5)}
									</> : <>
										<b>{a.bank}</b> **{a.card_number.slice(-4)}
									</>}
								</div>
								<div className="name">{a.name_account}</div>
							</div>
						</div>
					}}
				/>
				{shownModal===Modals.ACCOUNTS && (
					<>
						<Items
							data={[0]}
							selectable
							render={()=>{
								return <Link to={'/accounts'} target='_blank' style={{ textDecoration: 'none', color: 'inherit', width: '100%' }}>
									<div style={{ fontSize: 18, fontWeight: 'bold', textAlign: 'center', padding: 5 }}>Agregar Cuenta</div>
								</Link> 
							}}
						/>
						<Items
							data={[0]}
							selectable
							onSelected={bindClick(setShownModal, Modals.SEARCH_WORKER)}
							render={()=>{
								return <div style={{ fontSize: 18, fontWeight: 'bold', textAlign: 'center', padding: 5, width: '100%' }}>Buscar cuenta</div>
							}}
						/>
					</>
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={bindClick(setShownModal, shownModal===Modals.WORKER_ACCOUNTS ? Modals.ACCOUNTS : null)} />
			</Modal.Actions>
		</Modal>
	</>
}

export default ExpenseModal;
